import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { platformSyncApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Link2, CheckCircle, Clock, ExternalLink, Download, Copy, ChevronDown, ChevronUp, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const PLATFORMS = [
  { value: 'airbnb', label: 'Airbnb', color: 'bg-rose-500/20 text-rose-400 border-rose-500/30', helpUrl: 'https://www.airbnb.fr/help/article/99' },
  { value: 'booking', label: 'Booking.com', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', helpUrl: 'https://partner.booking.com/fr/aide' },
];

const STATUS_BADGES = {
  actif: { className: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  pending: { className: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  syncing: { className: 'bg-blue-500/20 text-blue-400', icon: RefreshCw },
};

function ExportRow({ name, url }) {
  const [expanded, setExpanded] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copiée dans le presse-papier');
    } catch {
      toast.error('Impossible de copier');
    }
  };
  return (
    <div className="border border-border/40 rounded-sm" data-testid={`export-row-${name}`}>
      <div className="p-3 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm font-medium flex items-center gap-2">
            <Download className="h-3.5 w-3.5 text-primary" /> {name}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={copy} data-testid="copy-ical-url-btn">
              <Copy className="h-3.5 w-3.5 mr-2" /> Copier l'URL
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <code className="text-[11px] bg-muted/30 px-2 py-1.5 rounded-sm break-all text-muted-foreground font-mono">{url}</code>
        {expanded && (
          <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-border/40 text-xs">
            <div className="space-y-2">
              <p className="font-medium text-rose-400 flex items-center gap-1.5">🏠 Airbnb</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                <li>Connectez-vous sur Airbnb (hôte)</li>
                <li>Annonces → votre annonce → Calendrier → Disponibilité</li>
                <li>« Synchroniser des calendriers » → « Connecter un autre site »</li>
                <li>Collez l'URL ci-dessus + nommez "Era Lodge"</li>
                <li>Validez</li>
              </ol>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-blue-400 flex items-center gap-1.5">🏨 Booking.com</p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                <li>Extranet Booking.com</li>
                <li>Calendrier & Tarifs → Synchronisation</li>
                <li>Connecter un calendrier</li>
                <li>Collez l'URL ci-dessus</li>
                <li>Validez</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PlatformSyncPage() {
  const { properties } = useOutletContext();
  const [syncs, setSyncs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [formData, setFormData] = useState({ property_id: '', platform: 'airbnb', ical_url: '', sync_name: '' });

  useEffect(() => { loadSyncs(); }, []);

  const loadSyncs = async () => {
    setLoading(true);
    try {
      const response = await platformSyncApi.getAll();
      setSyncs(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Veuillez sélectionner une propriété');
      return;
    }
    try {
      await platformSyncApi.create(formData);
      toast.success('Connexion créée');
      setDialogOpen(false);
      setFormData({ property_id: '', platform: 'airbnb', ical_url: '', sync_name: '' });
      loadSyncs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleTriggerSync = async (id) => {
    setSyncing((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await platformSyncApi.trigger(id);
      toast.success(`${response.data.imported} nouvelle(s) réservation(s) importée(s)`);
      loadSyncs();
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette connexion ?')) return;
    try {
      await platformSyncApi.delete(id);
      toast.success('Connexion supprimée');
      loadSyncs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getPropertyName = (propertyId) => properties.find((p) => p.id === propertyId)?.name || 'Propriété inconnue';
  const getPlatformInfo = (platform) => PLATFORMS.find((p) => p.value === platform) || PLATFORMS[0];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-sync-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Synchronisation</h1>
          <p className="text-muted-foreground mt-1 text-sm">Connectez vos calendriers Airbnb et Booking</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow" data-testid="add-sync-button">
              <Plus className="h-4 w-4 mr-2" />Connecter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Nouvelle connexion</DialogTitle>
            </DialogHeader>
            {properties.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-4">Vous devez d'abord créer une propriété avant de connecter un calendrier.</p>
                <Button type="button" variant="secondary" onClick={() => { setDialogOpen(false); window.location.href = '/proprietes'; }}>
                  Créer une propriété
                </Button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Propriété *</Label>
                <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })}>
                  <SelectTrigger data-testid="sync-property-select" className="w-full"><SelectValue placeholder="Sélectionner une propriété" /></SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px] overflow-y-auto z-[100]">
                    {properties.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plateforme</Label>
                <Select value={formData.platform} onValueChange={(v) => setFormData({ ...formData, platform: v })}>
                  <SelectTrigger data-testid="sync-platform-select" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent position="popper" className="z-[100]">
                    {PLATFORMS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>URL iCal *</Label>
                <Input type="url" value={formData.ical_url} onChange={(e) => setFormData({ ...formData, ical_url: e.target.value })} placeholder="https://..." required data-testid="sync-ical-url" />
                <p className="text-xs text-muted-foreground">Trouvez cette URL dans les paramètres de votre calendrier</p>
              </div>

              <div className="space-y-2">
                <Label>Nom (optionnel)</Label>
                <Input value={formData.sync_name} onChange={(e) => setFormData({ ...formData, sync_name: e.target.value })} placeholder="Ex: Calendrier principal" data-testid="sync-name-input" />
              </div>

              {/* Help links */}
              <div className="p-3 bg-muted/30 rounded-sm space-y-2">
                <p className="text-sm font-medium">Où trouver l'URL iCal ?</p>
                {PLATFORMS.map((p) => (
                  <a key={p.value} href={p.helpUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                    {p.label} - Guide<ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>

              <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sync-submit-button">
                Connecter
              </Button>
            </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <RefreshCw className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Synchronisation iCal bidirectionnelle</p>
              <p className="text-xs text-muted-foreground">Importez les réservations Airbnb/Booking ET partagez votre calendrier Era Lodge avec eux pour éviter les doubles réservations.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Era Lodge → Airbnb/Booking */}
      <Card className="bg-card border-border/40">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ArrowUpFromLine className="h-5 w-5 text-primary" />
            Exporter Era Lodge → Airbnb / Booking
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Collez cette URL dans Airbnb pour qu'il bloque automatiquement les dates réservées chez vous.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {properties.length === 0 ? (
            <p className="text-sm text-muted-foreground">Créez d'abord une propriété pour obtenir son URL iCal.</p>
          ) : (
            properties.map((prop) => {
              const exportUrl = `${window.location.origin}/api/ical/export/${prop.id}.ics`;
              return (
                <ExportRow key={prop.id} name={prop.name} url={exportUrl} />
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Section title for imports */}
      <div className="flex items-center gap-3 pt-2">
        <ArrowDownToLine className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-lg">Importer Airbnb / Booking → Era Lodge</h2>
      </div>

      {/* Syncs List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="bg-card border-border/40 animate-pulse">
              <CardContent className="py-4"><div className="h-5 bg-muted rounded w-1/3 mb-3" /><div className="h-4 bg-muted rounded w-1/2" /></CardContent>
            </Card>
          ))}
        </div>
      ) : syncs.length === 0 ? (
        <Card className="bg-card border-border/40">
          <CardContent className="py-12 text-center">
            <Link2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-heading font-medium mb-2">Aucune connexion</h3>
            <p className="text-muted-foreground text-sm mb-4">Connectez vos calendriers pour importer automatiquement vos réservations</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />Ajouter une connexion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {syncs.map((sync) => {
            const platformInfo = getPlatformInfo(sync.platform);
            const statusInfo = STATUS_BADGES[sync.status] || STATUS_BADGES.pending;
            const StatusIcon = statusInfo.icon;

            return (
              <Card key={sync.id} className="bg-card border-border/40 hover:border-primary/30 transition-colors" data-testid={`sync-card-${sync.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-sm flex items-center justify-center text-lg ${platformInfo.color}`}>
                        {sync.platform === 'airbnb' ? '🏠' : '🏨'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm">{sync.sync_name || `${platformInfo.label}`}</h3>
                          <Badge className={`${statusInfo.className} text-xs`}>
                            <StatusIcon className="h-3 w-3 mr-1" />{sync.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{getPropertyName(sync.property_id)}</p>
                        {sync.last_sync && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Dernière sync: {format(parseISO(sync.last_sync), 'd MMM HH:mm', { locale: fr })} · {sync.events_count} événements
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => handleTriggerSync(sync.id)} disabled={syncing[sync.id]} data-testid={`trigger-sync-${sync.id}`}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncing[sync.id] ? 'animate-spin' : ''}`} />
                        {syncing[sync.id] ? 'Sync...' : 'Synchroniser'}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(sync.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8" data-testid={`delete-sync-${sync.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
